import React, { useState, useEffect } from "react";
import { TextField, Button, Box, Chip, Autocomplete, Typography } from "@mui/material";
import {useAuth} from "../components/TokenHandler";

const CreateUserForm = ({ user, setUser, onClose, refreshUsers  }) => {
    const [availableRoles, setAvailableRoles] = useState([]);
    const [loading, setLoading] = useState(false);

    const {
        keycloak: kc,
        authenticated,
        infoMessage,
        setInfoMessage,
        givenName,
        email,
        roles,
    } = useAuth();

    // Fetch roles from the API
    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/get-keycloak-roles`, {
                    method: "GET",
                    headers: {
                        "Authorization": `Bearer ${kc.token}`, // Include the token in the Authorization header
                        "Content-Type": "application/json"
                    },
                });
                if (response.ok) {
                    const data = await response.json();
                    const formattedRoles = data.map((role) => ({
                        id: role.id,
                        name: role.name,
                        description: role.description,
                    }));
                    setAvailableRoles(formattedRoles);
                } else {
                    console.error("Failed to fetch roles:", response.statusText);
                }
            } catch (error) {
                console.error("Error fetching roles:", error);
            }
        };

        fetchRoles();
    }, []);

    const handleChangeRoles = (event, newValue) => {
        setUser((prev) => ({
            ...prev,
            roles: newValue,
        }));
    };

    const handleCreate = async () => {
        setLoading(true);

        // Step 1: Prepare the user creation payload
        const userCreationPayload = {
            enabled: true,
            username: user.email,
            firstName: user.first_name,
            lastName: user.last_name,
            email: user.email,
            credentials: [
                {
                    type: "password",
                    value: user.password,
                    temporary: true,
                },
            ],
        };

        try {
            // Step 2: Create the user
            const createUserResponse = await fetch(`${process.env.REACT_APP_API_URL}/create-keycloak-user`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${kc.token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(userCreationPayload),
            });

            if (createUserResponse.status === 201) {
                const createUserData = await createUserResponse.json();
                console.log(createUserData.message);

                // Step 3: Assign roles to the user
                if (user.roles && user.roles.length > 0) {
                    const roleAssignmentPayload = {
                        userEmail: user.email,
                        roleData: user.roles.map((role) => ({
                            id: role.id,
                            name: role.name,
                            description: role.description,
                        })),
                    };

                    const assignRolesResponse = await fetch(`${process.env.REACT_APP_API_URL}/assign-keycloak-role`, {
                        method: "PUT",
                        headers: {
                            "Authorization": `Bearer ${kc.token}`,
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(roleAssignmentPayload),
                    });

                    if (assignRolesResponse.status === 200) {
                        const assignRolesData = await assignRolesResponse.json();
                        console.log(assignRolesData.message);

                        // Step 4: Create user record in the database
                        const userRecordPayload = {
                            first_name: user.first_name,
                            last_name: user.last_name,
                            email: user.email,
                            roles: user.roles.map((role) => role.name), // Extract role names
                            attributes: user.roles.reduce((acc, role) => {
                                acc[role.name] = ""; // Initialize empty values for each role
                                return acc;
                            }, {}),
                        };

                        const createUserRecordResponse = await fetch(`${process.env.REACT_APP_API_URL}/create-user`, {
                            method: "POST",
                            headers: {
                                "Authorization": `Bearer ${kc.token}`,
                                "Content-Type": "application/json",
                            },
                            body: JSON.stringify(userRecordPayload),
                        });

                        if (createUserRecordResponse.status === 201) {
                            const userRecordData = await createUserRecordResponse.json();
                            console.log(userRecordData.message);
                            alert("User created, roles assigned, and user record added successfully!");
                            refreshUsers(); // Refresh the user list
                            onClose(); // Close the modal
                        } else {
                            console.error("Failed to create user record:", createUserRecordResponse.statusText);
                            alert("User created and roles assigned, but user record could not be added.");
                        }
                    } else {
                        console.error("Failed to assign roles:", assignRolesResponse.statusText);
                        alert("User created, but roles could not be assigned.");
                    }
                } else {
                    alert("User created successfully! No roles assigned.");
                }
            } else {
                console.error("Failed to create user:", createUserResponse.statusText);
                alert("Failed to create user.");
            }
        } catch (error) {
            console.error("Error creating user or assigning roles:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <TextField
                label="First Name"
                variant="outlined"
                fullWidth
                margin="normal"
                value={user.first_name || ""}
                onChange={(e) => setUser((prev) => ({ ...prev, first_name: e.target.value }))}
            />
            <TextField
                label="Last Name"
                variant="outlined"
                fullWidth
                margin="normal"
                value={user.last_name || ""}
                onChange={(e) => setUser((prev) => ({ ...prev, last_name: e.target.value }))}
            />
            <TextField
                label="Email"
                variant="outlined"
                fullWidth
                margin="normal"
                value={user.email || ""}
                onChange={(e) => setUser((prev) => ({ ...prev, email: e.target.value }))}
            />
            <TextField
                label="Password"
                variant="outlined"
                fullWidth
                margin="normal"
                type="password"
                value={user.password || ""}
                onChange={(e) => setUser((prev) => ({ ...prev, password: e.target.value }))}
            />
            <Autocomplete
                multiple
                options={availableRoles}
                getOptionLabel={(option) => `${option.name} - ${option.description}`}
                value={user.roles || []}
                onChange={handleChangeRoles}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                        <Chip
                            key={option.id}
                            label={option.name}
                            {...getTagProps({ index })}
                        />
                    ))
                }
                renderOption={(props, option) => (
                    <li {...props}>
                        <Box>
                            <Typography variant="body1" fontWeight="bold">
                                {option.name}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {option.description}
                            </Typography>
                        </Box>
                    </li>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant="outlined"
                        label="Roles"
                        placeholder="Search or select roles"
                        margin="normal"
                    />
                )}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: 2 }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCreate}
                    disabled={loading}
                >
                    {loading ? "Processing..." : "Create User"}
                </Button>
            </Box>
        </Box>
    );
};

export default CreateUserForm;
